import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import getDayRangeString from "../../../utils/day-range-string";
import getSelectedMetrics from "../../../utils/get-selected-metrics";

import { PERIOD_FORMATS } from "../../../constants";
import { useRillContext } from "../../../rill/RillReact/share/base";

const PERIOD_OPTIONS = (t) => ({
  day: [{ text: t("volumeSplitCoreLift.daily"), value: "day" }],
  week: [{ text: t("volumeSplitCoreLift.weekly"), value: "week" }],
  month: [{ text: t("volumeSplitCoreLift.monthly"), value: "month" }],
});

function View(props) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  _.defaults(locals.query, {
    type: "total",
    units: _.get(locals.snapshot.user, "units", "metric"),
  });

  const { snapshot, query, momentTZ } = locals;

  const { units } = query;
  const user = _.get(snapshot, "user", {});
  const exercisesVolume = _.get(snapshot, "data.exercisesVolume", {});
  const period = (query.period = _.get(snapshot, "data.period", "day"));
  const period_short = PERIOD_FORMATS[period];
  const startDate = momentTZ(_.get(snapshot, "data.start", momentTZ()));
  const endDate = momentTZ(_.get(snapshot, "data.end", momentTZ()));
  const selectedMetricsVolume = getSelectedMetrics(exercisesVolume, query.type);
  const keys = _(selectedMetricsVolume)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);
  const selectedMetricsVolumeGroups = _.values(selectedMetricsVolume);
  const totalMetricVolumes = _.map(keys, (key) =>
    _.sumBy(selectedMetricsVolumeGroups, `${key}.volume`)
  );
  const totalVolumes = _.map(totalMetricVolumes, (volume) =>
    locals.fromMetric(volume, units)
  );
  const maxVolume = _.max(totalVolumes);

  const volumeSeries = _.map(selectedMetricsVolume, (metrics, primary) => {
    return {
      name: primary,
      className: primary,
      data: _.map(keys, (key) =>
        locals.fromMetric(_.get(metrics, `${key}.volume`, 0), units)
      ),
    };
  });
  const intensitySeries = _.chain(keys)
    .map((key, i) => {
      const total = totalMetricVolumes[i];
      const metrics = _.compact(_.map(selectedMetricsVolumeGroups, key));
      const average = _.reduce(
        metrics,
        (prev, metric) => prev + metric.average * (metric.volume / total),
        0
      );
      const peak = _.max(_.map(metrics, "peak")) || 0;
      return [
        { x: key, y: average },
        { x: key, y: peak },
      ];
    })
    .unzip()
    .value();

  const intensitySeriesValues = _.map(_.flatten(intensitySeries), "y");
  const intensitySeriesMax = Math.max(
    Math.round((_.max(intensitySeriesValues) * 100) / 10) * 10,
    100
  );
  const intensitySeriesMin = Math.min(
    Math.max(Math.round((_.min(intensitySeriesValues) * 100) / 10) * 10, 0),
    40
  );

  return (
    <div className="body metrics">
      <h1 className="alt">{t("volumeSplitCoreLift.title")}</h1>
      <p>
        {t("volumeSplitCoreLift.description")}
      </p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="period"
                options={PERIOD_OPTIONS(t)[period]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("volumeSplitCoreLift.kg"), value: "metric" },
                  { text: t("volumeSplitCoreLift.lbs"), value: "imperial" },
                ]}
              />
            </div>
          </div>

          <div className="left-side">
            <div className="group">
              <ToggleLinksInput
                className="tabs"
                name="type"
                options={[
                  { text: t("volumeSplitCoreLift.total"), value: "total" },
                  { text: t("volumeSplitCoreLift.squat"), value: "squat" },
                  { text: t("volumeSplitCoreLift.bench"), value: "bench" },
                  { text: t("volumeSplitCoreLift.deadlift"), value: "deadlift" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="body">
          {!_.isEmpty(selectedMetricsVolume) && (
            <div>
              <div className="legend">
                <div className="line series-a">{t("volumeSplitCoreLift.avgIntensity")}</div>
                <div className="line series-b">{t("volumeSplitCoreLift.peakIntensity")}</div>
              </div>
              <div className="axis-labels">
                <div className="left">{t("volumeSplitCoreLift.volume")}</div>
                <div className="right">{t("volumeSplitCoreLift.intensity")}</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: volumeSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        onlyInteger: true,
                        scaleMinSpace: 30,
                        high: maxVolume,
                        low: 0,
                      },
                    }}
                  />

                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: [] }}
                    options={{
                      width: "60px",
                      height: "380px",
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        type: process.browser && require("chartist").StepAxis,
                        stretch: true,
                        showGrid: false,
                        showLabel: true,
                        ticks: _.range(
                          intensitySeriesMin,
                          intensitySeriesMax + 10,
                          10
                        ).map((i) => `${i}%`),
                      },
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: labels,
                      series: volumeSeries,
                    }}
                    options={{
                      width: `${_.size(keys) * 40}px`,
                      height: "380px",
                      stackBars: true,
                      chartPadding: { left: 35, right: 75 },
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                        high: maxVolume,
                        low: 0,
                      },
                    }}
                    tooltip={(item, i) => {
                      const key = keys[i];
                      const date = startDate.clone().add(key, "days");
                      const dayRange = getDayRangeString(
                        [
                          date.clone().startOf(period),
                          date.clone().endOf(period),
                        ],
                        query
                      );
                      const avgIntensity = _.get(
                        _.find(intensitySeries[0], { x: key }),
                        "y",
                        0
                      );
                      const peakIntensity = _.get(
                        _.find(intensitySeries[1], { x: key }),
                        "y",
                        0
                      );
                      const totalVolume = totalVolumes[i];
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            <div>
                              {t("volumeSplitCoreLift.avgIntensity")}{" "}
                              <b>{_.round(avgIntensity * 100)}%</b>
                            </div>
                            <div>
                              {t("volumeSplitCoreLift.peakIntensity")}{" "}
                              <b>{_.round(peakIntensity * 100)}%</b>
                            </div>
                            {_.compact(
                              _.map(volumeSeries, ({ name, data }) => {
                                if (!data[i]) return;
                                return (
                                  <div key={name}>
                                    {_.capitalize(name)}{" "}
                                    <b>
                                      {locals.withWeight(data[i], units)} -{" "}
                                      {_.round((data[i] / totalVolume) * 100)}%
                                    </b>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />
                  {_.size(keys) >= 2 && (
                    <Chart
                      type="Line"
                      data={{ series: intensitySeries }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        showPoint: false,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          low: intensitySeriesMin / 100,
                          high: intensitySeriesMax / 100,
                          showGrid: false,
                          showLabel: false,
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default View;
